// Full settings page (like chrome://settings): navigation left, content right.
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Database, Eye, EyeOff, KeyRound, Copy, MonitorSmartphone, Server, Shield, SlidersHorizontal, Trash2, User } from 'lucide-react';
import type { Api, Credential, SiteDataRow, SitePermission } from '../core/api';
import { t, tKind } from '../core/i18n';
import type { PermissionBroker } from '../core/permissions';
import { platform } from '../core/platform';
import { prefs } from '../core/prefs';
import { hostOf } from '../core/url';

type Section = 'general' | 'passwords' | 'data' | 'permissions' | 'session' | 'account' | 'server';

const MODE_KEY = 'gcb.mode';
const readMode = () => {
  try {
    return localStorage.getItem(MODE_KEY) === 'cloud' ? 'cloud' : 'direct';
  } catch {
    return 'direct';
  }
};

function Toggle(p: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="row toggle-row">
      <span className="grow site">{p.label}</span>
      <input type="checkbox" checked={p.checked} onChange={(e) => p.onChange(e.target.checked)} />
    </label>
  );
}

export function SettingsPage(p: { api: Api; broker: PermissionBroker; onClose: () => void; onEndSession: () => void; onLogout: () => void }) {
  const [section, setSection] = useState<Section>('general');
  const [list, setList] = useState<SitePermission[] | null>(null);
  const [server, setServer] = useState(p.api.base);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [mode, setMode] = useState(readMode());
  const [offerSave, setOfferSave] = useState(prefs.offerSave());
  const [offerFill, setOfferFill] = useState(prefs.offerFill());
  const [note, setNote] = useState('');

  // passwords
  const [creds, setCreds] = useState<Credential[] | null>(null);
  const [never, setNever] = useState<string[]>([]);
  const [shown, setShown] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState({ site: '', user: '', pass: '' });

  // site data
  const [sites, setSites] = useState<SiteDataRow[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [alsoPw, setAlsoPw] = useState(false);
  const [alsoPerm, setAlsoPerm] = useState(true);
  const [confirmAll, setConfirmAll] = useState(false);

  // account
  const [pw, setPw] = useState({ current: '', next: '' });

  const flash = (s: string) => {
    setNote(s);
    setTimeout(() => setNote(''), 2500);
  };

  const loadPerms = () => p.api.permissions().then(setList).catch(() => setList([]));
  const loadCreds = () =>
    p.api
      .passwords()
      .then((r) => {
        setCreds(r.passwords);
        setNever(Array.isArray(r.neverSave) ? r.neverSave : []);
      })
      .catch(() => setCreds([]));
  const loadSites = () => p.api.siteData().then(setSites).catch(() => setSites([]));

  useEffect(() => {
    loadPerms();
  }, []);
  useEffect(() => {
    if (section === 'passwords') loadCreds();
    if (section === 'data') loadSites();
  }, [section]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && p.onClose();
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [p.onClose]);

  const sections: { id: Section; label: string; icon: typeof Shield }[] = [
    { id: 'general', label: t('general'), icon: SlidersHorizontal },
    { id: 'passwords', label: t('passwords'), icon: KeyRound },
    { id: 'data', label: t('siteData'), icon: Database },
    { id: 'permissions', label: t('sitePermissions'), icon: Shield },
    { id: 'session', label: t('session'), icon: MonitorSmartphone },
    { id: 'account', label: t('account'), icon: User },
    ...(platform().needsServerUrl ? [{ id: 'server' as Section, label: t('server'), icon: Server }] : []),
  ];

  const reveal = async (c: Credential) => {
    if (shown[c.id] !== undefined) {
      setShown(({ [c.id]: _, ...rest }) => rest);
      return;
    }
    const pwd = await p.api.revealPassword(c.id).catch(() => '');
    setShown((s) => ({ ...s, [c.id]: pwd }));
  };

  const deleteSites = async (opts: { all?: boolean }) => {
    if (opts.all) await p.api.deleteSiteData({ all: true, permissions: alsoPerm, passwords: alsoPw });
    else for (const host of picked) await p.api.deleteSiteData({ host, permissions: alsoPerm, passwords: alsoPw });
    setPicked(new Set());
    setConfirmAll(false);
    flash(t('deletedOk'));
    loadSites();
    loadPerms();
  };

  const clearDevice = async () => {
    try {
      const r = await p.api.req<{ origin: string | null; port: number }>('POST', '/api/direct/token');
      const origin = r.origin || `${location.protocol}//${location.hostname}:${r.port}`;
      await new Promise<void>((resolve) => {
        const f = document.createElement('iframe');
        f.style.display = 'none';
        const done = () => {
          removeEventListener('message', onMsg);
          f.remove();
          resolve();
        };
        const onMsg = (e: MessageEvent) => e.origin === origin && e.data?.gcb === 'cleared' && done();
        addEventListener('message', onMsg);
        setTimeout(done, 8000);
        f.src = origin + '/clear.html';
        document.body.appendChild(f);
      });
      flash(t('deletedOk'));
    } catch {
      flash(t('errorGeneric'));
    }
  };

  const grouped = useMemo(() => {
    const m = new Map<string, Credential[]>();
    for (const c of creds ?? []) m.set(c.origin, [...(m.get(c.origin) ?? []), c]);
    return [...m.entries()];
  }, [creds]);

  return (
    <div className="settings" role="dialog" aria-label={t('settings')}>
      <div className="settings-head">
        <button className="icon-btn" aria-label={t('backToBrowser')} title={t('backToBrowser')} onClick={p.onClose}>
          <ArrowLeft size={16} />
        </button>
        <h1>{t('settings')}</h1>
        {note && <span className="note">{note}</span>}
      </div>
      <div className="settings-body">
        <nav className="settings-nav">
          {sections.map((s) => (
            <button key={s.id} className={section === s.id ? 'active' : ''} onClick={() => setSection(s.id)}>
              <s.icon size={15} />
              {s.label}
            </button>
          ))}
        </nav>

        <div className="settings-content">
          {section === 'general' && (
            <>
              <h2>{t('general')}</h2>
              <p className="muted">{t('browsingModeHint')}</p>
              <div className="rows">
                <div className="row">
                  <div className="grow site">{t('browsingMode')}</div>
                  <select
                    className="input select"
                    value={mode}
                    onChange={(e) => {
                      const v = e.target.value === 'cloud' ? 'cloud' : 'direct';
                      try {
                        localStorage.setItem(MODE_KEY, v);
                      } catch {
                        /* storage unavailable */
                      }
                      setMode(v);
                      location.reload();
                    }}
                  >
                    <option value="direct">{t('modeDirectOpt')}</option>
                    <option value="cloud">{t('modeCloudOpt')}</option>
                  </select>
                </div>
                <Toggle label={t('offerSavePw')} checked={offerSave} onChange={(v) => (prefs.set('offerSave', v), setOfferSave(v))} />
                <Toggle label={t('offerFillPw')} checked={offerFill} onChange={(v) => (prefs.set('offerFill', v), setOfferFill(v))} />
              </div>
              <h2 style={{ marginTop: 28 }}>{t('deviceData')}</h2>
              <p className="muted">{t('deviceDataHint')}</p>
              <div className="rows">
                <div className="row">
                  <div className="grow site">{t('clearDevice')}</div>
                  <button className="btn danger-text" onClick={clearDevice}>
                    {t('deleteBtn')}
                  </button>
                </div>
              </div>
            </>
          )}

          {section === 'passwords' && (
            <>
              <h2>{t('passwords')}</h2>
              <p className="muted">{t('passwordsHint')}</p>
              <div className="rows">
                {creds === null ? (
                  <div className="empty">
                    <span className="spinner" />
                  </div>
                ) : grouped.length === 0 ? (
                  <div className="empty">{t('noPasswords')}</div>
                ) : (
                  grouped.map(([origin, items]) =>
                    items.map((c) => (
                      <div className="row" key={c.id}>
                        <div className="grow">
                          <div className="site">{hostOf(origin)}</div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {c.username || '–'}
                            {shown[c.id] !== undefined && <span className="mono"> · {shown[c.id]}</span>}
                          </div>
                        </div>
                        <button className="icon-btn" title={shown[c.id] !== undefined ? t('hide') : t('show')} aria-label={shown[c.id] !== undefined ? t('hide') : t('show')} onClick={() => reveal(c)}>
                          {shown[c.id] !== undefined ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                        <button
                          className="icon-btn"
                          title={t('copyPw')}
                          aria-label={t('copyPw')}
                          onClick={async () => {
                            const pwd = shown[c.id] ?? (await p.api.revealPassword(c.id).catch(() => ''));
                            if (pwd) navigator.clipboard?.writeText(pwd).then(() => flash(t('copied')), () => {});
                          }}
                        >
                          <Copy size={15} />
                        </button>
                        <button
                          className="icon-btn"
                          title={t('deleteBtn')}
                          aria-label={t('deleteBtn')}
                          onClick={async () => {
                            await p.api.deletePassword(c.id);
                            loadCreds();
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )),
                  )
                )}
              </div>

              <h3 style={{ marginTop: 24 }}>{t('addPassword')}</h3>
              <form
                className="add-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  let origin = '';
                  try {
                    origin = new URL(/^https?:\/\//i.test(adding.site) ? adding.site : 'https://' + adding.site).origin;
                  } catch {
                    return flash(t('errorGeneric'));
                  }
                  await p.api.savePassword(origin, adding.user, adding.pass).then(
                    () => {
                      setAdding({ site: '', user: '', pass: '' });
                      flash(t('pwSaved'));
                      loadCreds();
                    },
                    () => flash(t('errorGeneric')),
                  );
                }}
              >
                <input className="input" placeholder={t('website')} value={adding.site} onChange={(e) => setAdding({ ...adding, site: e.target.value })} autoCapitalize="off" spellCheck={false} required />
                <input className="input" placeholder={t('username')} value={adding.user} onChange={(e) => setAdding({ ...adding, user: e.target.value })} autoCapitalize="off" spellCheck={false} autoComplete="off" />
                <input className="input" type="password" placeholder={t('password')} value={adding.pass} onChange={(e) => setAdding({ ...adding, pass: e.target.value })} autoComplete="new-password" required />
                <button className="btn primary" type="submit">
                  {t('saveBtn')}
                </button>
              </form>

              {never.length > 0 && (
                <>
                  <h3 style={{ marginTop: 24 }}>{t('neverSavedFor')}</h3>
                  <div className="rows">
                    {never.map((o) => (
                      <div className="row" key={o}>
                        <div className="grow site">{hostOf(o)}</div>
                        <button
                          className="btn"
                          onClick={async () => {
                            await p.api.setNeverSave(o, false);
                            loadCreds();
                          }}
                        >
                          {t('removeBtn')}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {section === 'data' && (
            <>
              <h2>{t('siteData')}</h2>
              <p className="muted">{t('siteDataHint')}</p>
              <div className="rows">
                {sites === null ? (
                  <div className="empty">
                    <span className="spinner" />
                  </div>
                ) : sites.length === 0 ? (
                  <div className="empty">{t('noSiteData')}</div>
                ) : (
                  <>
                    <div className="row head-row">
                      <div className="grow muted">{t('website')}</div>
                      <span className="col muted">{t('cookiesCol')}</span>
                      <span className="col muted">{t('passwordsCol')}</span>
                      <span className="col muted">{t('permissionsCol')}</span>
                    </div>
                    {sites.map((s) => (
                      <label className="row" key={s.host}>
                        <input
                          type="checkbox"
                          checked={picked.has(s.host)}
                          onChange={(e) =>
                            setPicked((cur) => {
                              const n = new Set(cur);
                              if (e.target.checked) n.add(s.host);
                              else n.delete(s.host);
                              return n;
                            })
                          }
                        />
                        <div className="grow site">{s.host}</div>
                        <span className="col mono">{s.cookies}</span>
                        <span className="col mono">{s.passwords}</span>
                        <span className="col mono">{s.permissions}</span>
                      </label>
                    ))}
                  </>
                )}
              </div>
              <div className="data-actions">
                <Toggle label={t('alsoPermissions')} checked={alsoPerm} onChange={setAlsoPerm} />
                <Toggle label={t('alsoPasswords')} checked={alsoPw} onChange={setAlsoPw} />
                <div className="actions-row" style={{ marginTop: 12 }}>
                  <button className="btn danger-text" disabled={picked.size === 0} onClick={() => deleteSites({})}>
                    {t('deleteSelected')}
                    {picked.size > 0 ? ` (${picked.size})` : ''}
                  </button>
                  {confirmAll ? (
                    <span className="confirm">
                      <button className="btn" onClick={() => setConfirmAll(false)}>
                        {t('cancel')}
                      </button>
                      <button className="btn danger-text" onClick={() => deleteSites({ all: true })}>
                        {t('confirm')}
                      </button>
                    </span>
                  ) : (
                    <button className="btn danger-text" onClick={() => setConfirmAll(true)}>
                      {t('deleteEverything')}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {section === 'permissions' && (
            <>
              <h2>{t('sitePermissions')}</h2>
              <p className="muted">{t('permissionsHint')}</p>
              <div className="rows">
                {list === null ? (
                  <div className="empty">
                    <span className="spinner" />
                  </div>
                ) : list.length === 0 ? (
                  <div className="empty">{t('noPermissions')}</div>
                ) : (
                  list.map((perm) => (
                    <div className="row" key={perm.origin + perm.kind}>
                      <div className="grow">
                        <div className="site">{hostOf(perm.origin)}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {tKind(perm.kind)}
                        </div>
                      </div>
                      <span className={'state' + (perm.allow ? '' : ' blocked')}>{perm.allow ? t('allowed') : t('blocked')}</span>
                      <button
                        className="btn"
                        onClick={async () => {
                          await p.broker.forget(perm.origin, perm.kind);
                          loadPerms();
                        }}
                      >
                        {t('revoke')}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {section === 'session' && (
            <>
              <h2>{t('session')}</h2>
              <p className="muted">{t('sessionHint')}</p>
              <div className="rows">
                <div className="row">
                  <div className="grow">
                    <div className="site">{t('endSession')}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {t('endSessionHint')}
                    </div>
                  </div>
                  {confirmEnd ? (
                    <span className="confirm">
                      <button className="btn" onClick={() => setConfirmEnd(false)}>
                        {t('cancel')}
                      </button>
                      <button className="btn danger-text" onClick={p.onEndSession}>
                        {t('confirm')}
                      </button>
                    </span>
                  ) : (
                    <button className="btn danger-text" onClick={() => setConfirmEnd(true)}>
                      {t('endSession')}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {section === 'account' && (
            <>
              <h2>{t('account')}</h2>
              <p className="muted" />
              <div className="rows">
                <div className="row">
                  <div className="grow site">{t('logout')}</div>
                  <button className="btn" onClick={p.onLogout}>
                    {t('logout')}
                  </button>
                </div>
              </div>
              <h3 style={{ marginTop: 24 }}>{t('changePassword')}</h3>
              <form
                className="add-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await p.api.changePassword(pw.current, pw.next).then(
                    () => {
                      setPw({ current: '', next: '' });
                      flash(t('pwChanged'));
                    },
                    (err) => flash(err?.message ?? t('errorGeneric')),
                  );
                }}
              >
                <input className="input" type="password" placeholder={t('currentPassword')} value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" required />
                <input className="input" type="password" placeholder={t('newPassword')} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} minLength={6} autoComplete="new-password" required />
                <button className="btn primary" type="submit">
                  {t('saveBtn')}
                </button>
              </form>
            </>
          )}

          {section === 'server' && (
            <>
              <h2>{t('server')}</h2>
              <p className="muted">{t('serverHint')}</p>
              <div style={{ maxWidth: 420 }}>
                <label className="label" htmlFor="srv">
                  {t('serverUrl')}
                </label>
                <input id="srv" className="input" value={server} onChange={(e) => setServer(e.target.value)} inputMode="url" autoCapitalize="off" spellCheck={false} />
                <button
                  className="btn primary"
                  style={{ marginTop: 12 }}
                  onClick={() => {
                    p.api.setServer(server);
                    location.reload();
                  }}
                >
                  {t('saveServer')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
